import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { FieldValues, Path, UseFormReturn, useWatch } from "react-hook-form";
import localeCore from "../../core/locale.core";
import { useTranslation } from "../../core/translation.core";
import ChevronDownIcon from "../../icons/chevronDown.icon";
import { FancyCheckbox } from "../reuseables/form.comp";
import tagCore from "../../core/tag.core";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import { Tag } from "../../types/tag";

export default function TagMultipleSelectorComp<T extends FieldValues>({
  methods,
  name,
  labelMaxLength = 150,
}: {
  methods: UseFormReturn<T, any>;
  name: Path<T>;
  labelMaxLength?: number;
}) {
  const [isOpened, setIsOpened] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { t, msg } = useTranslation();

  useEffect(() => {
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  function onClick(e: MouseEvent) {
    // This is a hack to get the latest state
    if (!ref || !ref.current) return;
    const isOpened = (ref.current as any).getAttribute("data-state");

    if (!isOpened) return;

    const isWithin = (e.target as any).closest("#tag-select");

    if (!isWithin) {
      setIsOpened(false);
    }
  }

  const css =
    "relative " +
    "h-9 w-full " +
    "dark:bg-her-backblue " +
    `border rounded-t-md ${
      !isOpened ? "rounded-b-md" : ""
    } hover:border-blue-200 hover:border-2 focus-visible:border-blue-600 focus-visible:border-2 ` +
    "focus-visible:outline-none outline-none " +
    "focus-visible:shadow-sm focus-visible:shadow-blue-800 " +
    "duration-1000 transition-[box-shadow] " +
    "disabled:text-her-lightgray2 text-base ";

  return (
    <div id="tag-select" ref={ref} data-state={isOpened} className={css}>
      <button
        type="button"
        className="w-full h-full flex justify-between pr-2 pl-4 items-center tag-button"
        onClick={() => setIsOpened((value) => !value)}
      >
        {t(msg.tag.selectTags)}
      </button>
      {isOpened && <Panel methods={methods} name={name} />}
      <div className="absolute right-2 top-1">
        <ChevronDownIcon />
      </div>
    </div>
  );
}

function Panel<T extends FieldValues>({ methods, name }: { methods: UseFormReturn<T, any>; name: Path<T> }) {
  const { t, msg } = useTranslation();
  const {
    data: tags,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ["tags"],
    queryFn: tagCore.listTags,
  });

  const [searchTerm, setSearchTerm] = useState("");

  if (isError) return <ErrorCenterIcon reason={error.message} />;
  if (isPending) return <LoadingOvalCenterIcon />;
  if (!tags) return null;

  const filteredTags = tags.filter((tag) =>
    localeCore.getLocaleValueFallback(tag.name, "en")?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div
      id="tag-panel"
      className="tag_panel absolute top-[34px] left-0 w-full border rounded-b-md bg-white dark:bg-her-backblue space-y-1 px-4 py-4 max-h-72 overflow-y-auto"
    >
      <input
        type="text"
        placeholder={t(msg.tag.searchTags)}
        className="w-full p-2 mb-2 border bg-white rounded-md focus:border-white focus:outline-none"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      {filteredTags.length > 0 ? (
        filteredTags.map((c) => <TagRow key={c.id} tag={c} methods={methods} name={name} />)
      ) : (
        <div className="text-center text-gray-500">No tags found</div>
      )}
    </div>
  );
}

function TagRow<T extends FieldValues>({ tag, methods, name }: { tag: Tag; methods: UseFormReturn<T, any>; name: Path<T> }) {
  const { locale } = useRouter();

  return (
    <div className="w-full text-left">
      <FancyCheckbox labelId="tag-labels" value={tag.id} register={methods.register(name)}>
        {localeCore.getLocaleValueFallback(tag.name, locale!)} ({tag.name.zhHant}) ({tag.name.zhHans})
      </FancyCheckbox>
    </div>
  );
}
